import { getLanguageFromIntlLocale } from "@core/i18n/locale-helper";
import { getPercentRatio } from "@core/i18n/percent-utils";
import { WordingKey } from "@typings/i18n-typings";
import { useCountryConfig } from "@ui/pages/patient-details/hooks/use-country-config";
import { colors } from "@ui/style/colors";
import { isNil, isNotNil } from "@utils/assert";
import { computeDiffBetween, computeDiffInDays, daysToMonth, now } from "@utils/date-utils";
import { getTwoDigitStringFromNumber, positiveModulo } from "@utils/number-utils";
import { ICUParams } from "@wording/icu-params";
import dayjs, { Dayjs } from "dayjs";
import { classNames } from "primereact/utils";
import React, { ReactNode, useCallback } from "react";
import { IntlShape, PrimitiveType, useIntl } from "react-intl";

enum DateFormats {
	FULL = "FULL",
	FULL_WITHOUT_YEAR = "FULL_WITHOUT_YEAR",
	FULL_WITH_WEEKDAY = "FULL_WITH_WEEKDAY",
	FULL_WITHOUT_YEAR_WITH_WEEKDAY = "FULL_WITHOUT_YEAR_WITH_WEEKDAY",
	MED = "MED",
	MED_WITHOUT_YEAR = "MED_WITHOUT_YEAR",
	MED_WITH_WEEKDAY = "MED_WITH_WEEKDAY",
	MED_WITHOUT_YEAR_WITH_WEEKDAY = "MED_WITHOUT_YEAR_WITH_WEEKDAY",
	SHORT = "SHORT",
}

export type DateFormatMapping = Record<DateFormats, string>;

export type NewLinkType = {
	onClick: (event: React.MouseEvent) => void;
	className?: string;
	dataTest?: string;
};

export type I18nFormat = <K extends WordingKey>(
	key: K,
	...values: ICUParams[K] extends undefined ? [undefined?] : [ICUParams[K]]
) => string;

type ValuesType =
	| Record<
		string,
		| string
		| number
		| boolean
		| Date
		| null
		| undefined
		| ((event: React.MouseEvent) => void)
		| JSX.Element
		| NewLinkType
	>
	| undefined;


const getTagFormatters = (values?: ValuesType) => ({
	color: (parts: ReactNode) => (
		<span style={{ color: (values?.color as string | undefined) ?? colors.primary }}>{parts}</span>
	),
	grey: (parts: ReactNode) => <span style={{ color: colors.grey }}>{parts}</span>,
	boldSmall: (parts: ReactNode) => <span className="p-size-s-bold">{parts}</span>,
	strong: (parts: ReactNode) => <span style={{ fontWeight: "bold" }}>{parts}</span>,
	link: (parts: ReactNode) => (
		<span
			data-test={(values?.link as NewLinkType).dataTest}
			onClick={(values?.link as NewLinkType).onClick}
			style={{ cursor: "pointer" }}
			className={(values?.link as NewLinkType).className}
		>
			{parts}
		</span>
	),
	link2: (parts: ReactNode) => (
		<span
			data-test={(values?.link2 as NewLinkType).dataTest}
			onClick={(values?.link2 as NewLinkType).onClick}
			style={{ cursor: "pointer" }}
			className={classNames((values?.link2 as NewLinkType).className)}
		>
			{parts}
		</span>
	),
	element: () => values?.element,
});

export interface UseI18nHook extends Omit<IntlShape, "formatRelativeTime" | "formatDate"> {
	language: string;
	format: I18nFormat;
	formatAge: (dateOfBirth: Dayjs) => string;
	formatDuration: (timeInSecond: number) => string;
	formatPercent: (numberInPercent: number) => string;
	formatProgramDuration: (date1: Dayjs, date2: Dayjs) => string;
	getEndWeekDay: () => string;
	formatDurationLeft: (duration: number, monthPrecisionThreshold?: number, calendarMode?: boolean) => string;
	//Date formats
	formatRelativeDateTime: (date: Dayjs) => string;
	formatDateRelative: (date: Dayjs) => string;
	formatDateFull: (date: Dayjs) => string;
	formatDateFullWithoutYear: (date: Dayjs) => string;
	formatDateFullWithWeekday: (date: Dayjs) => string;
	formatDateFullWithoutYearWithWeekday: (date: Dayjs) => string;
	formatDateMed: (date: Dayjs) => string;
	formatDateMedWithoutYear: (date: Dayjs) => string;
	formatDateMedWithWeekday: (date: Dayjs) => string;
	formatDateMedWithoutYearWithWeekday: (date: Dayjs) => string;
	formatDateShort: (date: Dayjs | undefined) => string;
}
export function useI18n(): UseI18nHook {
	const intl = useIntl();
	const { countryConfig } = useCountryConfig();
	const format = useCallback(
		(key: WordingKey, values?: ValuesType) =>
			intl.formatMessage({ id: key }, { ...values, ...getTagFormatters(values) } as unknown as Record<
				string,
				PrimitiveType
			>),
		[intl],
	);

	const formatDuration = useCallback<UseI18nHook["formatDuration"]>(timeInSecond => {
		const { hour, minutes, seconds } = {
			hour: getTwoDigitStringFromNumber(Math.floor(timeInSecond / 3600)),
			minutes: getTwoDigitStringFromNumber(Math.floor((timeInSecond % 3600) / 60)),
			seconds: getTwoDigitStringFromNumber(Math.floor(timeInSecond % 60)),
		};
		return timeInSecond >= 3600 ? `${hour}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
	}, []);

	const formatAge = useCallback<UseI18nHook["formatAge"]>(birthDate => now().diff(birthDate, "years").toString(), []);

	const formatPercent = useCallback<UseI18nHook["formatPercent"]>(
		numberInPercent => {
			const ratio = getPercentRatio(numberInPercent);
			return intl.formatNumber(ratio, { style: "percent" });
		},
		[intl],
	);

	const formatRelativeDateTime = useCallback<UseI18nHook["formatRelativeDateTime"]>(
		date => {
			const duration = date.diff(now(), "minute");
			if (duration === 0) {
				return format("global.now").toLowerCase();
			}

			return date.fromNow();
		},
		[format],
	);

	const formatDateRelative = useCallback(
		(date: Dayjs): string => {
			const daysFromNow = computeDiffInDays(now(), date);
			switch (daysFromNow) {
				case -1:
					return format("primereact.calendar.yesterday");
				case 0:
					return format("primereact.calendar.today");
				case 1:
					return format("primereact.calendar.tomorrow");
				default:
					return date.fromNow();
			}
		},
		[format],
	);

	const formatUnit = useCallback(
		(givenUnit: number, unit: "day" | "week" | "month") => {
			switch (givenUnit) {
				case 0:
					return "";
				case 1:
					return format(RELATIVE_TIME_WORDING_RECORD[unit].singular);
				default:
					return format(RELATIVE_TIME_WORDING_RECORD[unit].plural).replace("%d", givenUnit.toString());
			}
		},
		[format],
	);

	const formatProgramDuration = useCallback<UseI18nHook["formatProgramDuration"]>(
		(date1, date2) => {
			const { days, weeks, month } = computeDiffBetween(date1, date2);

			if (days === 0 && month === 0 && weeks === 0) {
				return format("global.empty");
			}

			if (month === 0) {
				if (weeks === 0) {
					return formatUnit(days, "day");
				}
				if (days === 0) {
					return formatUnit(weeks, "week");
				}
				return [formatUnit(weeks, "week"), formatUnit(days, "day")].join(", ");
			}

			return [formatUnit(month, "month"), formatUnit(days + weeks * 7, "day")].join(", ");
		},
		[format, formatUnit],
	);

	const formatDurationLeft = useCallback<UseI18nHook["formatDurationLeft"]>(
		(durationInDays, monthPrecisionThreshold, calendarMode) => {
			const isMonthPrecision = isNotNil(monthPrecisionThreshold) && durationInDays > monthPrecisionThreshold;

			const durationInMonth = daysToMonth(durationInDays, calendarMode);
			const finalDuration = isMonthPrecision ? durationInMonth : durationInDays;

			const formattedTimeLeft = formatUnit(finalDuration, isMonthPrecision ? "month" : "day");

			return durationInDays === 0
				? format("global.timeLeft.none")
				: format("global.timeLeft", {
					remainingTime: finalDuration,
					formattedRemainingTime: formattedTimeLeft,
				});
		},
		[format, formatUnit],
	);

	const getEndWeekDay = useCallback(
		() => dayjs.weekdays()[positiveModulo(dayjs.localeData().firstDayOfWeek() - 1, 7)],
		[],
	);

	const formatDate = useCallback(
		(date: Dayjs | undefined, dateFormat: DateFormats) => {
			if (isNil(countryConfig) || isNil(date)) {
				return "-";
			}
			return date.format(countryConfig.dateFormats[dateFormat]);
		},
		[countryConfig],
	);

	const formatDateFull = (date: Dayjs) => formatDate(date, DateFormats.FULL);
	const formatDateFullWithoutYear = (date: Dayjs) => formatDate(date, DateFormats.FULL_WITHOUT_YEAR);
	const formatDateFullWithWeekday = (date: Dayjs) => formatDate(date, DateFormats.FULL_WITH_WEEKDAY);
	const formatDateFullWithoutYearWithWeekday = (date: Dayjs) =>
		formatDate(date, DateFormats.FULL_WITHOUT_YEAR_WITH_WEEKDAY);
	const formatDateMed = (date: Dayjs) => formatDate(date, DateFormats.MED);
	const formatDateMedWithoutYear = (date: Dayjs) => formatDate(date, DateFormats.MED_WITHOUT_YEAR);
	const formatDateMedWithWeekday = (date: Dayjs) => formatDate(date, DateFormats.MED_WITH_WEEKDAY);
	const formatDateMedWithoutYearWithWeekday = (date: Dayjs) =>
		formatDate(date, DateFormats.MED_WITHOUT_YEAR_WITH_WEEKDAY);
	const formatDateShort = (date: Dayjs | undefined) => formatDate(date, DateFormats.SHORT);

	return {
		...intl,
		language: getLanguageFromIntlLocale(intl.locale),
		format,
		formatAge,
		formatDuration,
		formatPercent,
		formatRelativeDateTime,
		formatDateRelative,
		formatProgramDuration,
		getEndWeekDay,
		formatDurationLeft,
		formatDateFull,
		formatDateFullWithoutYear,
		formatDateFullWithoutYearWithWeekday,
		formatDateFullWithWeekday,
		formatDateMedWithoutYear,
		formatDateMedWithoutYearWithWeekday,
		formatDateShort,
		formatDateMedWithWeekday,
		formatDateMed,
	};
}

const RELATIVE_TIME_WORDING_RECORD: Record<
	"day" | "week" | "month",
	{
		singular: WordingKey;
		plural: WordingKey;
	}
> = {
	day: {
		singular: "global.relativeTime.day",
		plural: "global.relativeTime.days",
	},
	week: {
		singular: "global.relativeTime.week",
		plural: "global.relativeTime.weeks",
	},
	month: {
		singular: "global.relativeTime.month",
		plural: "global.relativeTime.months",
	},
};
